import React, { forwardRef, useState } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { Box } from "@/shared/ui/base";
import { cn } from "@/shared/lib/utils/cn";
import type { BaseComponentProps } from "@/shared/ui/base";

export interface InputProps
  extends
    BaseComponentProps,
    Omit<React.InputHTMLAttributes<HTMLInputElement>, "className"> {
  label?: string;
  error?: string;
  helperText?: string;
  icon?: React.ReactNode;
  variant?: "text" | "number" | "email" | "password" | "tel" | "date";
  showNumberControls?: boolean;
  onIncrement?: () => void;
  onDecrement?: () => void;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      icon,
      variant = "text",
      required,
      disabled,
      showNumberControls = false,
      onIncrement,
      onDecrement,
      className,
      "data-testid": dataTestId,
      id,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);

    // Generate unique ID if not provided
    const inputId = id || `input-${Math.random().toString(36).substring(2, 9)}`;

    const inputType = variant === "number" ? "text" : variant;

    const leftPadding = icon ? "pl-10" : "pl-4";
    const rightPadding = showNumberControls ? "pr-12" : "pr-4";

    const borderClass = error
      ? "border-red-500"
      : isFocused
        ? "border-red-500 shadow-[inset_0_0_0_1px_#ef4444]"
        : "border-gray-300 hover:border-gray-400";

    return (
      <Box className={className} data-testid={dataTestId}>
        {label && (
          <Box
            as="label"
            htmlFor={inputId}
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </Box>
        )}

        <Box className="relative">
          {/* Left Icon */}
          {icon && (
            <Box className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {icon}
            </Box>
          )}

          {/* Input Field */}
          <input
            ref={ref}
            id={inputId}
            type={inputType}
            inputMode={variant === "number" ? "numeric" : undefined}
            required={required}
            disabled={disabled}
            className={cn(
              "w-full py-3 border rounded-lg bg-white transition-colors outline-none",
              "disabled:bg-gray-100 disabled:cursor-not-allowed",
              leftPadding,
              rightPadding,
              borderClass,
            )}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            {...props}
          />

          {/* Number Controls */}
          {showNumberControls && variant === "number" && (
            <Box className="absolute right-0 inset-y-0 flex items-center">
              <Box className="flex flex-col border-l border-gray-300 h-full">
                <Box
                  as="button"
                  type="button"
                  onClick={onIncrement}
                  disabled={disabled}
                  className="flex-1 px-2 hover:bg-gray-100 flex items-center justify-center border-b border-gray-300 rounded-tr-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronUp className="w-4 h-4 text-gray-500" />
                </Box>
                <Box
                  as="button"
                  type="button"
                  onClick={onDecrement}
                  disabled={disabled}
                  className="flex-1 px-2 hover:bg-gray-100 flex items-center justify-center rounded-br-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* Helper Text or Error */}
        {(helperText || error) && (
          <Box
            as="p"
            className={cn(
              "mt-1 text-sm",
              error ? "text-red-500" : "text-gray-500",
            )}
          >
            {error || helperText}
          </Box>
        )}
      </Box>
    );
  },
);

Input.displayName = "Input";
